import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { authFetch, useAuth } from "../../App";
import { offlineAuthFetch } from "../../lib/offlineAuthFetch";
import { storeOfflinePhoto } from "../../lib/offlineDB";
import { useOffline } from "../../hooks/useOffline";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  ListTodo, Plus, CheckCircle2, Clock, Circle, User,
  Camera, Layers, Image, X, AlertOctagon, Download,
  History, FileText, Edit3, ChevronDown, Save, WifiOff,
  Square, CheckSquare, Trash2, ChevronRight, Mic, MicOff, Loader2,
  ImagePlus, MapPin,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import TaskMarkerPicker from "../../components/TaskMarkerPicker";
import { useTranslation } from "../../i18n/LanguageProvider";

const API = "/api";
const STATUS_FLOW = ["offen", "in_arbeit", "erledigt"];

/* Fullscreen Photo Overlay */
function PhotoOverlay({ src, onClose }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="task-photo-overlay">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 z-10" data-testid="task-photo-overlay-close">
        <X className="w-6 h-6" />
      </button>
      <img src={src} alt="Originalbild" className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} />
    </div>
  );
}
const STATUS_CFG = {
  offen:     { bg: "bg-white/10", text: "text-gray-400", icon: Circle,       label: "Offen" },
  in_arbeit: { bg: "bg-amber-500/20", text: "text-amber-400", icon: Clock,      label: "In Arbeit" },
  erledigt:  { bg: "bg-green-500/20", text: "text-green-400", icon: CheckCircle2, label: "Erledigt" },
  blockiert: { bg: "bg-red-500/20", text: "text-red-400", icon: AlertOctagon, label: "Blockiert" },
};
const HISTORY_LABELS = {
  status: "Status", title: "Titel", description: "Beschreibung",
  assigned_to: "Zuweisung", priority: "Priorität",
  photo_added: "Foto hinzugefügt", photo_deleted: "Foto gelöscht",
  field_changed: "Feld geändert",
};

/* ───── Compress image before upload ───── */
const MAX_FILE_SIZE_MB = 10;
function compressImage(file, quality = 0.75, maxW = 1600) {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/") || file.type === "image/gif") { resolve(file); return; }
    const img = new window.Image();
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxW) { h = Math.round((h * maxW) / w); w = maxW; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        resolve(blob ? new File([blob], file.name, { type: "image/jpeg" }) : file);
      }, "image/jpeg", quality);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

/* ───── Photo Source Picker (Camera vs Gallery) ─────
   iOS-Safari-Pattern (bulletproof): Der `<input type="file">` wird ABSOLUT POSITIONIERT
   und transparent über die sichtbare Karten-Fläche gelegt. So tippt der User DIREKT
   auf den Input (nicht via Label-Forwarding) — das ist das einzige Pattern, das
   in allen iOS-Versionen zuverlässig den nativen Datei-Picker öffnet.
   `display:none`/`className="hidden"` Inputs werden von iOS oft NICHT durch Label
   getriggert — daher diese Overlay-Variante.
*/
function PhotoSourcePicker({ open, onClose, onFilesSelected }) {
  // Reset input nach Auswahl, damit dieselbe Datei erneut wählbar ist
  const handleChange = (e) => {
    onFilesSelected(e);
    // Reset auf dem aktuellen Element (input)
    try { e.target.value = ""; } catch (_err) { /* iOS Safari may throw on reset */ }
  };
  if (!open) return null;
  if (typeof document === "undefined") return null;
  // Stil für den unsichtbaren File-Input, der den Touch-Bereich exakt abdeckt.
  const overlayInputStyle = {
    position: "absolute",
    top: 0, left: 0, right: 0, bottom: 0,
    width: "100%", height: "100%",
    opacity: 0,
    cursor: "pointer",
    fontSize: 0,           // verhindert Safari-Browse-Button-Größenprobleme
    WebkitTapHighlightColor: "transparent",
    zIndex: 2,
  };
  // → PORTAL nach document.body, damit `position:fixed` zuverlässig am Viewport ankert
  //   (sonst bricht es in iOS Safari, sobald ein Vorfahre `transform`/`will-change`
  //   gesetzt hat — z.B. animate-in slide-from-right Mobile-Übergänge).
  return createPortal(
    <div
      className="flex items-end justify-center"
      onClick={onClose}
      data-testid="photo-source-picker"
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 2147483000, // höher als alles andere (Bottom-Nav, Dialogs, etc.)
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-full max-w-md pb-3 px-3 animate-in slide-in-from-bottom-4 duration-200" onClick={e => e.stopPropagation()} style={{ paddingBottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.75rem))" }}>
        <div className="bg-[#1c1f26] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-sm font-semibold text-gray-200 text-center">Foto hinzufuegen</p>
          </div>

          {/* ── Foto aufnehmen ── Input direkt über der Karte (transparent) */}
          <div className="relative" data-testid="photo-source-camera">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleChange}
              style={overlayInputStyle}
              aria-label="Foto aufnehmen"
              data-testid="photo-source-camera-input"
            />
            <div className="flex items-center gap-3 w-full px-4 py-3.5 text-left active:bg-white/10 transition-colors pointer-events-none">
              <div className="p-2 rounded-xl bg-blue-500/15">
                <Camera className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-200">Foto aufnehmen</p>
                <p className="text-[11px] text-gray-500">Kamera oeffnen</p>
              </div>
            </div>
          </div>

          {/* ── Aus Galerie wählen ── */}
          <div className="relative border-t border-white/5" data-testid="photo-source-gallery">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleChange}
              style={overlayInputStyle}
              aria-label="Aus Galerie waehlen"
              data-testid="photo-source-gallery-input"
            />
            <div className="flex items-center gap-3 w-full px-4 py-3.5 text-left active:bg-white/10 transition-colors pointer-events-none">
              <div className="p-2 rounded-xl bg-emerald-500/15">
                <ImagePlus className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-200">Aus Galerie waehlen</p>
                <p className="text-[11px] text-gray-500">Mehrfachauswahl moeglich</p>
              </div>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full mt-2 py-3 bg-[#1c1f26] rounded-2xl text-sm font-semibold text-gray-400 active:text-white border border-white/10 transition-colors"
          data-testid="photo-source-cancel"
        >
          Abbrechen
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ───── Translation Banner — zeigt dezent „Automatisch übersetzt …" mit Original-Toggle ───── */
function TranslationBanner({ task }) {
  const [showOriginal, setShowOriginal] = useState(false);
  // Nichts anzeigen wenn keine Übersetzung
  if (task.display_language === "translated" || task.translation_status === "auto" || task.translation_status === "manual_reviewed") {
    const isManual = task.translation_reviewed;
    return (
      <div className="bg-blue-500/8 border border-blue-500/20 rounded-lg px-2.5 py-1.5 text-[10px] text-blue-300/80" data-testid="translation-banner">
        <div className="flex items-center justify-between gap-2">
          <span className="flex-1">
            {isManual ? "Manuell geprüfte Übersetzung" : "Automatisch übersetzt aus Deutsch"}
          </span>
          <button onClick={() => setShowOriginal(s => !s)} className="text-blue-400 underline-offset-2 hover:underline text-[10px]" data-testid="toggle-original">
            {showOriginal ? "Übersetzung" : "Original"}
          </button>
        </div>
        {showOriginal && (
          <div className="mt-1.5 pt-1.5 border-t border-blue-500/20 space-y-0.5" data-testid="original-text-block">
            <div className="text-[11px] font-semibold text-gray-300">{task.title_original}</div>
            {task.area_location_original && <div className="text-[10px] text-amber-300/80 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {task.area_location_original}</div>}
            {task.description_original && <div className="text-[10px] text-gray-400 whitespace-pre-line">{task.description_original}</div>}
          </div>
        )}
      </div>
    );
  }
  if (task.translation_status === "failed") {
    return (
      <div className="bg-amber-500/8 border border-amber-500/20 rounded-lg px-2.5 py-1.5 text-[10px] text-amber-300/80" data-testid="translation-banner-failed">
        Automatische Übersetzung derzeit nicht verfügbar. Originaltext wird angezeigt.
      </div>
    );
  }
  return null;
}



/* ───── Task Kommentare — bidirektionale Übersetzung ─────
   Sk-User schreibt SK → Ronny liest DE.  DE-User schreibt DE → Lukas liest SK.
*/
function TaskCommentsSection({ projectId, taskId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [showOriginalIds, setShowOriginalIds] = useState({});

  const load = useCallback(async () => {
    if (!projectId || !taskId) return;
    setLoading(true);
    const res = await authFetch(`${API}/projects/${projectId}/tasks/${taskId}/comments`);
    if (res.ok) {
      const d = await res.json();
      setComments(Array.isArray(d) ? d : []);
    }
    setLoading(false);
  }, [projectId, taskId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = text.trim();
    if (!t || posting) return;
    setPosting(true);
    const res = await authFetch(`${API}/projects/${projectId}/tasks/${taskId}/comments`, {
      method: "POST",
      body: JSON.stringify({ text: t }),
    });
    if (res.ok) {
      const d = await res.json();
      setComments(prev => [...prev, d]);
      setText("");
    } else {
      toast.error("Kommentar konnte nicht gesendet werden");
    }
    setPosting(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Kommentar löschen?")) return;
    const res = await authFetch(`${API}/projects/${projectId}/tasks/${taskId}/comments/${id}`, { method: "DELETE" });
    if (res.ok) setComments(prev => prev.filter(c => c.id !== id));
  };

  const userLang = user?.language || "de";

  return (
    <div className="border-t border-white/10 pt-4" data-testid="task-comments-section">
      <Label className="text-xs text-gray-500 mb-2 block">Kommentare / Rückmeldungen</Label>

      <div className="space-y-2 mb-3">
        {loading && <div className="text-xs text-gray-500">Lädt…</div>}
        {!loading && comments.length === 0 && (
          <div className="text-[11px] text-gray-600 italic">Noch keine Kommentare.</div>
        )}
        {comments.map(c => {
          const isTranslated = c.display_language && c.display_language !== c.original_language;
          const showOrig = showOriginalIds[c.id];
          const isOwn = c.user_id === user?.id;
          return (
            <div key={c.id} className="bg-white/5 border border-white/10 rounded-lg p-2.5" data-testid={`task-comment-${c.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-gray-300">{c.user_name}</span>
                <div className="flex items-center gap-2 text-[9px] text-gray-500">
                  <span>{c.created_at ? format(new Date(c.created_at), "dd.MM. HH:mm", { locale: de }) : ""}</span>
                  {isOwn && (
                    <button onClick={() => remove(c.id)} className="text-red-400 hover:text-red-300" data-testid={`del-comment-${c.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[12px] text-gray-200 whitespace-pre-line">{showOrig ? c.original_text : c.display_text}</p>
              {isTranslated && (
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-[9px] text-blue-300/70">
                    {c.translation_reviewed ? "Manuell geprüft" : `Automatisch übersetzt aus ${(c.original_language || "").toUpperCase()}`}
                  </span>
                  <button
                    onClick={() => setShowOriginalIds(p => ({ ...p, [c.id]: !showOrig }))}
                    className="text-[10px] text-blue-400 underline-offset-2 hover:underline"
                    data-testid={`toggle-orig-${c.id}`}
                  >
                    {showOrig ? "Übersetzung" : "Original"}
                  </button>
                </div>
              )}
              {c.translation_status === "failed" && !isTranslated && (
                <div className="mt-1 text-[9px] text-amber-300/70">Automatische Übersetzung derzeit nicht verfügbar.</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2">
        <Textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={userLang === "sk" ? "Napíšte komentár…" : userLang === "en" ? "Write a comment…" : "Kommentar schreiben…"}
          rows={2}
          className="flex-1 bg-white/10 border-white/15 text-white placeholder:text-gray-500 text-sm"
          data-testid="new-comment-input"
        />
        <button
          onClick={submit}
          disabled={posting || !text.trim()}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          data-testid="post-comment-btn"
        >
          {posting ? "…" : "Senden"}
        </button>
      </div>
    </div>
  );
}


function DarkProgressBar({ tasks }) {
  const total = tasks.length;
  const done = tasks.filter(t => t.status === "erledigt").length;
  const inProg = tasks.filter(t => t.status === "in_arbeit").length;
  if (total === 0) return null;
  return (
    <div data-testid="m-task-progress">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-400">Fortschritt</span>
        <span className="text-xs font-bold text-white">{done}/{total}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden flex">
        {done > 0 && <div className="bg-green-500 transition-all duration-500" style={{ width: `${(done / total) * 100}%` }} />}
        {inProg > 0 && <div className="bg-amber-500 transition-all duration-500" style={{ width: `${(inProg / total) * 100}%` }} />}
      </div>
      <div className="flex gap-3 mt-1.5 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500" />{done}</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{inProg}</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white/20" />{total - done - inProg}</span>
      </div>
    </div>
  );
}

/* ───── Dark Task Card ───── */
function DarkTaskCard({ task, onTapStatus, onSelect, onPhotoClick }) {
  const cfg = STATUS_CFG[task.status] || STATUS_CFG.offen;
  const Icon = cfg.icon;
  return (
    <div className="bg-white/[0.04] border border-white/8 rounded-xl overflow-hidden active:bg-white/[0.08] transition-colors" data-testid={`m-task-card-${task.id}`}>
      <div className="flex">
        <button
          onClick={(e) => { e.stopPropagation(); onTapStatus(task); }}
          className={`w-14 flex-shrink-0 flex flex-col items-center justify-center gap-1 ${cfg.bg} border-r border-white/8 active:opacity-60`}
          data-testid={`m-status-tap-${task.id}`}
        >
          <Icon className={`w-5 h-5 ${cfg.text}`} />
          <span className={`text-[8px] font-bold ${cfg.text} leading-none`}>{cfg.label}</span>
        </button>
        <div className="flex-1 p-3 min-w-0" onClick={() => onSelect(task)}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-gray-600 font-mono">{task.task_number}</p>
              <h3 className="font-semibold text-white text-[14px] leading-snug line-clamp-2">{task.title}</h3>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {task.photo_count > 0 && (
                <span className="flex items-center gap-0.5 text-[10px] text-gray-500"><Image className="w-3 h-3" />{task.photo_count}</span>
              )}
              <button onClick={(e) => { e.stopPropagation(); onPhotoClick(task); }}
                className="p-1.5 rounded-lg bg-white/5 active:bg-white/15" data-testid={`m-photo-btn-${task.id}`}>
                <Camera className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
          {task.area_location && (
            <p className="text-[11px] text-amber-300/80 flex items-center gap-1 mt-0.5" data-testid={`m-task-area-${task.id}`}>
              <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="line-clamp-1">{task.area_location}</span>
            </p>
          )}
          {task.task_location_marker?.plan_id && (
            <p className="text-[11px] text-blue-300 flex items-center gap-1 mt-0.5" data-testid={`m-task-marker-${task.id}`}>
              <MapPin className="w-2.5 h-2.5 flex-shrink-0 fill-blue-400" />
              <span>Standort markiert</span>
            </p>
          )}
          {task.description && <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{task.description}</p>}
          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500">
            <User className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">
              {(() => {
                const names = (task.assigned_users_names && task.assigned_users_names.length)
                  ? task.assigned_users_names.filter(Boolean)
                  : (task.assigned_to_name ? [task.assigned_to_name] : []);
                if (names.length === 0) return "Nicht zugewiesen";
                if (names.length <= 2) return names.join(", ");
                return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
              })()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───── Checklist / Unteraufgaben Section ───── */
function ChecklistSection({ task, setTask, projectId, onTaskUpdated, reloadTask }) {
  const [newItem, setNewItem] = useState("");
  const [adding, setAdding] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [overlayPhoto, setOverlayPhoto] = useState(null);
  const clCameraRef = useRef(null);
  const clGalleryRef = useRef(null);
  const [showClPicker, setShowClPicker] = useState(false);
  const [pickerItemId, setPickerItemId] = useState(null);

  const checklist = task.checklist || [];
  const doneCount = checklist.filter(c => c.checked).length;

  const addItem = async () => {
    if (!newItem.trim()) return;
    setAdding(true);
    const item = {
      id: `cl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      title: newItem.trim(),
      checked: false,
      photos: [],
      created_at: new Date().toISOString(),
    };
    const updated = [...checklist, item];
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}`,
      { method: "PATCH", body: JSON.stringify({ checklist: updated }) },
      { type: "task-checklist", meta: { taskId: task.id }, optimisticResponse: { ...task, checklist: updated } }
    );
    if (res.ok) {
      setTask(prev => ({ ...prev, checklist: updated }));
      onTaskUpdated({ ...task, checklist: updated });
      setNewItem("");
    } else toast.error("Fehler");
    setAdding(false);
  };

  const toggleItem = async (itemId) => {
    const updated = checklist.map(c =>
      c.id === itemId ? { ...c, checked: !c.checked } : c
    );
    setTask(prev => ({ ...prev, checklist: updated }));
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}`,
      { method: "PATCH", body: JSON.stringify({ checklist: updated }) },
      { type: "task-checklist", meta: { taskId: task.id }, optimisticResponse: { ...task, checklist: updated } }
    );
    if (!res.ok) {
      setTask(prev => ({ ...prev, checklist }));
      toast.error("Fehler");
    }
  };

  const removeItem = async (itemId) => {
    const updated = checklist.filter(c => c.id !== itemId);
    setTask(prev => ({ ...prev, checklist: updated }));
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}`,
      { method: "PATCH", body: JSON.stringify({ checklist: updated }) },
      { type: "task-checklist", meta: { taskId: task.id }, optimisticResponse: { ...task, checklist: updated } }
    );
    if (res.ok) {
      onTaskUpdated({ ...task, checklist: updated });
    } else {
      setTask(prev => ({ ...prev, checklist }));
      toast.error("Fehler");
    }
  };

  const handleItemPhoto = async (e, itemId) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingItemId(itemId);
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error(`${file.name} zu gross (max ${MAX_FILE_SIZE_MB} MB)`);
          continue;
        }
        const compressed = await compressImage(file, 0.75, 1600);
        if (!navigator.onLine) {
          await storeOfflinePhoto({ blob: compressed, filename: file.name, projectId, taskId: task.id, checklistItemId: itemId });
          toast.success("Foto lokal gespeichert");
        } else {
          const fd = new FormData();
          fd.append("file", compressed);
          const res = await authFetch(
            `${API}/projects/${projectId}/tasks/${task.id}/checklist/${itemId}/photos`,
            { method: "POST", body: fd, headers: {} }
          );
          if (res.ok) {
            toast.success("Foto gespeichert");
          } else {
            const err = await res.json();
            toast.error(err.detail || "Upload fehlgeschlagen");
          }
        }
      }
      await reloadTask();
    } catch { toast.error("Fehler"); }
    finally {
      setUploadingItemId(null);
      if (clCameraRef.current) clCameraRef.current.value = "";
      if (clGalleryRef.current) clGalleryRef.current.value = "";
    }
  };

  const deleteItemPhoto = async (itemId, photoId) => {
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}/checklist/${itemId}/photos/${photoId}`,
      { method: "DELETE" },
      { type: "checklist-photo-delete", meta: { taskId: task.id, itemId, photoId } }
    );
    if (res.ok) {
      toast.success("Foto gelöscht");
      await reloadTask();
    }
  };

  return (
    <div className="border-t border-white/10 pt-4" data-testid="checklist-section">
      {overlayPhoto && <PhotoOverlay src={overlayPhoto} onClose={() => setOverlayPhoto(null)} />}
      <Label className="text-xs text-gray-500 flex items-center gap-1.5 mb-3">
        <CheckSquare className="w-3.5 h-3.5" />
        Checkliste {checklist.length > 0 && `(${doneCount}/${checklist.length})`}
      </Label>

      {/* Progress bar */}
      {checklist.length > 0 && (
        <div className="w-full h-1.5 bg-white/10 rounded-full mb-3 overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-300"
            style={{ width: `${checklist.length > 0 ? (doneCount / checklist.length) * 100 : 0}%` }}
          />
        </div>
      )}

      {/* Items */}
      <div className="space-y-1.5 mb-3">
        {checklist.map((item) => {
          const photos = item.photos || [];
          const isExpanded = expandedItem === item.id;
          return (
            <div key={item.id} className="group" data-testid={`cl-item-${item.id}`}>
              <div className="flex items-start gap-2 py-1.5 px-2 rounded-lg hover:bg-white/5 transition-colors">
                {/* Checkbox */}
                <button
                  onClick={() => toggleItem(item.id)}
                  className="mt-0.5 flex-shrink-0"
                  data-testid={`cl-toggle-${item.id}`}
                >
                  {item.checked
                    ? <CheckSquare className="w-5 h-5 text-green-400" />
                    : <Square className="w-5 h-5 text-gray-500 hover:text-gray-300" />}
                </button>
                {/* Title + expand */}
                <button
                  onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                  className={`flex-1 text-left text-sm min-w-0 ${item.checked ? "line-through text-gray-600" : "text-gray-200"}`}
                >
                  {item.title}
                  {photos.length > 0 && (
                    <span className="ml-1.5 text-[10px] text-blue-400 font-medium">
                      {photos.length} Foto{photos.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </button>
                {/* Camera + Delete */}
                <div className="flex items-center gap-1 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => { setExpandedItem(item.id); setPickerItemId(item.id); setShowClPicker(true); }}
                    className="p-1 rounded hover:bg-white/10 text-blue-400"
                    data-testid={`cl-photo-btn-${item.id}`}
                  >
                    <Camera className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="p-1 rounded hover:bg-white/10 text-red-400"
                    data-testid={`cl-delete-${item.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Expanded: Photos */}
              {isExpanded && (
                <div className="ml-7 mt-1 mb-2">
                  {uploadingItemId === item.id && (
                    <div className="text-xs text-blue-400 mb-1 animate-pulse">Hochladen...</div>
                  )}
                  {photos.length > 0 ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      {photos.map(p => (
                        <div key={p.id} className="relative rounded-md overflow-hidden border border-white/10 group/ph">
                          <img
                            src={p.url}
                            alt={p.filename}
                            className="w-full h-16 object-cover cursor-pointer"
                            onClick={() => setOverlayPhoto(p.url)}
                            data-testid={`cl-photo-view-${p.id}`}
                          />
                          <button
                            onClick={() => deleteItemPhoto(item.id, p.id)}
                            className="absolute top-0.5 right-0.5 p-0.5 bg-red-500/80 rounded text-white opacity-0 group-hover/ph:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-600">Keine Fotos</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add item input */}
      <div className="flex gap-2">
        <Input
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") addItem(); }}
          placeholder="Neuer Punkt..."
          className="bg-white/10 border-white/15 text-white text-sm placeholder:text-gray-600 h-9"
          data-testid="cl-new-item-input"
        />
        <button
          onClick={addItem}
          disabled={adding || !newItem.trim()}
          className="px-3 h-9 bg-blue-600 text-white rounded-md text-sm font-medium disabled:opacity-40 flex items-center gap-1"
          data-testid="cl-add-item-btn"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Hidden file inputs for checklist photos */}
      <input
        ref={clCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { if (pickerItemId) handleItemPhoto(e, pickerItemId); }}
      />
      <input
        ref={clGalleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { if (pickerItemId) handleItemPhoto(e, pickerItemId); }}
      />
      <PhotoSourcePicker
        open={showClPicker}
        onClose={() => setShowClPicker(false)}
        onFilesSelected={(e) => { setShowClPicker(false); if (pickerItemId) handleItemPhoto(e, pickerItemId); }}
      />
    </div>
  );
}

/* ───── Full-Featured Task Detail Modal ───── */
function TaskDetailModal({ task: initialTask, open, onClose, projectId, onTaskUpdated, users }) {
  const [task, setTask] = useState(initialTask);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sendingToReport, setSendingToReport] = useState(false);
  const [overlayPhoto, setOverlayPhoto] = useState(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  // Reload task data when opened or after changes
  const reloadTask = useCallback(async () => {
    if (!projectId || !initialTask?.id) return;
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/worker-tasks?filter_type=all`, {},
      { cacheKey: `tasks-${projectId}` }
    );
    if (res.ok) {
      const d = await res.json();
      const found = (d.tasks || []).find(t => t.id === initialTask.id);
      if (found) { setTask(found); onTaskUpdated(found); }
    }
  }, [projectId, initialTask?.id, onTaskUpdated]);

  useEffect(() => { if (initialTask) { setTask(initialTask); setEditing(false); setShowHistory(false); } }, [initialTask]);

  const startEdit = () => {
    // Edit immer am ORIGINAL (DE), nicht an der übersetzten Anzeige
    const auArr = Array.isArray(task.assigned_users) && task.assigned_users.length
      ? task.assigned_users
      : (task.assigned_to ? [task.assigned_to] : []);
    setEditForm({
      title: task.title_original || task.title,
      description: task.description_original || task.description || "",
      area_location: task.area_location_original || task.area_location || "",
      assigned_users: auArr,
      task_location_marker: task.task_location_marker || null,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    const body = {};
    const origTitle = task.title_original || task.title;
    const origDesc = task.description_original || task.description || "";
    const origArea = task.area_location_original || task.area_location || "";
    const origAuArr = Array.isArray(task.assigned_users) && task.assigned_users.length
      ? task.assigned_users
      : (task.assigned_to ? [task.assigned_to] : []);
    if (editForm.title !== origTitle) body.title = editForm.title;
    if (editForm.description !== origDesc) body.description = editForm.description;
    if ((editForm.area_location || "") !== origArea) body.area_location = editForm.area_location || "";
    // Multi-Zuweisung: vergleiche als Set
    const newAu = editForm.assigned_users || [];
    const auChanged = newAu.length !== origAuArr.length || newAu.some(id => !origAuArr.includes(id));
    if (auChanged) {
      body.assigned_users = newAu;
      body.assigned_to = newAu.length ? newAu[0] : null; // Primärer Assignee = erster
    }
    // Marker-Änderungen
    const oldM = task.task_location_marker || null;
    const newM = editForm.task_location_marker || null;
    const markerChanged = JSON.stringify(oldM) !== JSON.stringify(newM);
    if (markerChanged && newM) body.task_location_marker = newM;
    if (Object.keys(body).length === 0 && !(markerChanged && !newM)) { setEditing(false); setSaving(false); return; }
    const res = Object.keys(body).length
      ? await offlineAuthFetch(
          `${API}/projects/${projectId}/tasks/${task.id}`,
          { method: "PATCH", body: JSON.stringify(body) },
          { type: "task-edit", meta: { taskId: task.id }, optimisticResponse: { ...task, ...body } }
        )
      : { ok: true, json: async () => ({}) };
    // Marker-Löschung
    if (markerChanged && !newM && oldM) {
      await offlineAuthFetch(
        `${API}/projects/${projectId}/tasks/${task.id}/marker`,
        { method: "DELETE" },
        { type: "task-marker-clear", meta: { taskId: task.id } }
      );
    }
    if (res.ok) {
      const data = await res.json();
      if (data.offline) {
        toast.success("Lokal gespeichert");
        setTask(prev => ({ ...prev, ...body, task_location_marker: newM }));
        onTaskUpdated({ ...task, ...body, task_location_marker: newM });
      } else {
        toast.success("Gespeichert");
        await reloadTask();
      }
      setEditing(false);
    } else toast.error("Fehler beim Speichern");
    setSaving(false);
  };

  const changeStatus = async (s) => {
    // Optimistic update
    const oldStatus = task.status;
    setTask(prev => ({ ...prev, status: s }));
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}`,
      { method: "PATCH", body: JSON.stringify({ status: s }) },
      { type: "task-status", meta: { taskId: task.id }, optimisticResponse: { ...task, status: s } }
    );
    if (res.ok) {
      const data = await res.json();
      if (data.offline) {
        toast.success("Status lokal gespeichert");
        onTaskUpdated({ ...task, status: s });
      } else {
        toast.success("Status aktualisiert");
        await reloadTask();
      }
    } else {
      setTask(prev => ({ ...prev, status: oldStatus }));
      toast.error("Fehler");
    }
  };

  // Photo upload – STAYS in modal (supports multiple files)
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error(`${file.name} zu gross (max ${MAX_FILE_SIZE_MB} MB)`);
          continue;
        }
        const compressed = await compressImage(file, 0.75, 1600);
        if (!navigator.onLine) {
          await storeOfflinePhoto({ blob: compressed, filename: file.name, projectId, taskId: task.id });
          toast.success("Foto lokal gespeichert (Upload bei Verbindung)");
        } else {
          const fd = new FormData(); fd.append("file", compressed);
          const res = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}/photos`, { method: "POST", body: fd, headers: {} });
          if (res.ok) { toast.success("Foto gespeichert"); }
          else { const err = await res.json(); toast.error(err.detail || "Upload fehlgeschlagen"); }
        }
      }
      await reloadTask();
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const deletePhoto = async (photoId) => {
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}/photos/${photoId}`,
      { method: "DELETE" },
      { type: "photo-delete", meta: { taskId: task.id, photoId } }
    );
    if (res.ok) { toast.success("Foto gelöscht"); await reloadTask(); }
  };

  // History
  const loadHistory = async () => {
    setLoadingHistory(true);
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}/history`, {},
      { cacheKey: `task-history-${task.id}` }
    );
    if (res.ok) { const d = await res.json(); setHistory(d.history || []); }
    setLoadingHistory(false);
    setShowHistory(true);
  };

  // Bautagesbericht
  const sendToReport = async () => {
    setSendingToReport(true);
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}/to-daily-report`,
      { method: "POST", body: JSON.stringify({ task_id: task.id, include_photos: true }) },
      { type: "to-report", meta: { taskId: task.id } }
    );
    if (res.ok) {
      const data = await res.json();
      toast.success(data.offline ? "Lokal gespeichert" : "In Bautagesbericht übernommen");
    } else { const err = await res.json(); toast.error(err.detail || "Fehler"); }
    setSendingToReport(false);
  };

  if (!task) return null;

  const statusOpts = [
    { v: "offen", l: "Offen", darkCls: "bg-gray-500/20 text-gray-300", ring: "ring-gray-400" },
    { v: "in_arbeit", l: "In Arbeit", darkCls: "bg-amber-500/20 text-amber-300", ring: "ring-amber-400" },
    { v: "erledigt", l: "Erledigt", darkCls: "bg-green-500/20 text-green-300", ring: "ring-green-400" },
  ];

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      {overlayPhoto && <PhotoOverlay src={overlayPhoto} onClose={() => setOverlayPhoto(null)} />}
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#1a1b2e] border-white/10 text-white" data-testid="task-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-gray-400 text-sm font-mono">{task.task_number}</DialogTitle>
            {!editing && (
              <button onClick={startEdit} className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 font-medium" data-testid="edit-task-btn">
                <Edit3 className="w-3.5 h-3.5" />Bearbeiten
              </button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title + Description – editable */}
          {editing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-gray-400">Titel</Label>
                <Input value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} className="bg-white/10 border-white/15 text-white placeholder:text-gray-500" data-testid="edit-title-input" />
              </div>
              <div>
                <Label className="text-gray-400">Beschreibung</Label>
                <Textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} rows={3} className="bg-white/10 border-white/15 text-white placeholder:text-gray-500" data-testid="edit-desc-input" />
              </div>
              <div>
                <Label className="text-gray-400">Bereich / Ort im Projekt (optional)</Label>
                <Input
                  value={editForm.area_location || ""}
                  onChange={e => setEditForm(p => ({ ...p, area_location: e.target.value }))}
                  placeholder="z. B. Hinterer Technikbereich, Halle 2"
                  className="bg-white/10 border-white/15 text-white placeholder:text-gray-500"
                  data-testid="edit-area-input"
                />
              </div>
              <div>
                <Label className="text-gray-400">Standort auf Plan (optional)</Label>
                <TaskMarkerPicker
                  projectId={projectId}
                  marker={editForm.task_location_marker}
                  onChange={(m) => setEditForm(p => ({ ...p, task_location_marker: m }))}
                  theme="dark"
                />
              </div>
              <div>
                <Label className="text-gray-400">Zuweisen an (Mehrfach möglich)</Label>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="edit-assign-chips">
                  {users.map(u => {
                    const selected = (editForm.assigned_users || []).includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setEditForm(p => {
                          const cur = p.assigned_users || [];
                          const next = cur.includes(u.id) ? cur.filter(x => x !== u.id) : [...cur, u.id];
                          return { ...p, assigned_users: next };
                        })}
                        className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selected
                            ? "bg-blue-500/30 border-blue-400/60 text-blue-100"
                            : "bg-white/5 border-white/10 text-gray-300 active:bg-white/10"
                        }`}
                        data-testid={`edit-assign-chip-${u.id}`}
                      >
                        {selected && "✓ "}{u.name}
                      </button>
                    );
                  })}
                </div>
                {(editForm.assigned_users || []).length === 0 && (
                  <p className="text-[11px] text-gray-500 mt-1">Niemand zugewiesen</p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={saveEdit} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                  data-testid="save-edit-btn">
                  <Save className="w-3.5 h-3.5" />{saving ? "Speichern..." : "Speichern"}
                </button>
                <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm text-gray-400 hover:bg-white/10 rounded-lg">Abbrechen</button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold text-white" data-testid="task-detail-title">{task.title}</h2>
              {task.area_location && <p className="text-xs text-amber-300/90 flex items-center gap-1.5" data-testid="task-detail-area"><MapPin className="w-3 h-3" /> {task.area_location}</p>}
              {task.task_location_marker?.plan_id && (
                <p className="text-xs text-blue-300 flex items-center gap-1.5" data-testid="task-detail-marker">
                  <MapPin className="w-3 h-3 fill-blue-400" /> Standort auf Plan markiert
                </p>
              )}
              {task.description && <p className="text-sm text-gray-400 whitespace-pre-line" data-testid="task-detail-desc">{task.description}</p>}
              {/* ── Translation banner — wenn der Anzeigetext übersetzt ist ── */}
              <TranslationBanner task={task} />
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <User className="w-3.5 h-3.5" />
                <span>{(task.assigned_users_names && task.assigned_users_names.length)
                  ? task.assigned_users_names.filter(Boolean).join(", ")
                  : (task.assigned_to_name || "Nicht zugewiesen")}</span>
              </div>
            </>
          )}

          {/* Status Buttons – Dark */}
          <div>
            <Label className="text-xs text-gray-500 mb-2 block">Status</Label>
            <div className="flex gap-2">
              {statusOpts.map(o => (
                <button key={o.v} onClick={() => changeStatus(o.v)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    task.status === o.v
                      ? `${o.darkCls} ring-2 ring-offset-1 ring-offset-[#1a1b2e] ${o.ring}`
                      : `${o.darkCls} opacity-40 hover:opacity-70`
                  }`}
                  data-testid={`m-detail-status-${o.v}`}
                >{o.l}</button>
              ))}
            </div>
          </div>

          {/* Checklist / Unteraufgaben */}
          <ChecklistSection
            task={task}
            setTask={setTask}
            projectId={projectId}
            onTaskUpdated={onTaskUpdated}
            reloadTask={reloadTask}
          />

          {/* Photo Section – always visible */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" />
                Fotos {task.photos?.length > 0 && `(${task.photos.length})`}
              </Label>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
              <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
              <button
                onClick={() => setShowPhotoPicker(true)}
                disabled={uploading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold active:bg-blue-700 disabled:opacity-50"
                data-testid="m-detail-photo-upload"
              >
                <ImagePlus className="w-3.5 h-3.5" />
                {uploading ? "Hochladen..." : "Foto hinzufuegen"}
              </button>
            </div>

            {task.photos?.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {task.photos.map((p) => (
                  <div key={p.id} className="relative group rounded-lg overflow-hidden border border-white/10">
                    <img
                      src={p.url}
                      alt={p.filename}
                      className="w-full h-28 object-cover cursor-pointer"
                      onClick={() => setOverlayPhoto(p.url)}
                      data-testid={`view-photo-${p.id}`}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-end justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                      <div className="flex gap-1 pb-2 pointer-events-auto">
                        <a href={`${API}/task-photos/${p.stored_filename || p.filename}/download`} download
                          className="p-1.5 bg-white/90 rounded-lg text-gray-700 hover:bg-white" data-testid={`download-photo-${p.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </a>
                        <button onClick={() => deletePhoto(p.id)}
                          className="p-1.5 bg-red-500/90 rounded-lg text-white hover:bg-red-600" data-testid={`delete-photo-${p.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[9px] text-gray-500 text-center py-1 truncate px-1">{p.filename}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 bg-white/5 rounded-lg border border-white/8">
                <Camera className="w-6 h-6 text-gray-600 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Noch keine Fotos</p>
              </div>
            )}
          </div>

          {/* Bautagesbericht + PDF Export */}
          <div className="border-t border-white/10 pt-4 space-y-2">
            <button
              onClick={sendToReport}
              disabled={sendingToReport}
              className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-white/20 rounded-xl text-sm font-medium text-gray-400 hover:border-blue-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors disabled:opacity-50"
              data-testid="to-daily-report-btn"
            >
              <FileText className="w-4 h-4" />
              {sendingToReport ? "Wird übernommen..." : "In Bautagesbericht übernehmen"}
            </button>
            <a
              href={`${API}/projects/${projectId}/bautagesbericht/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-3 border border-white/20 rounded-xl text-sm font-medium text-gray-400 hover:border-green-400 hover:text-green-400 hover:bg-green-500/10 transition-colors"
              data-testid="export-pdf-btn"
              onClick={(e) => {
                e.preventDefault();
                const token = localStorage.getItem("token");
                fetch(`${API}/projects/${projectId}/bautagesbericht/pdf`, {
                  headers: { Authorization: `Bearer ${token}` },
                }).then(r => r.blob()).then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `Bautagesbericht_${projectId}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("PDF heruntergeladen");
                }).catch(() => toast.error("PDF-Export fehlgeschlagen"));
              }}
            >
              <Download className="w-4 h-4" />
              PDF Bautagesbericht exportieren
            </a>
          </div>

          {/* History */}
          <div className="border-t border-white/10 pt-4">
            <button
              onClick={() => showHistory ? setShowHistory(false) : loadHistory()}
              className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 font-medium"
              data-testid="toggle-history-btn"
            >
              <History className="w-3.5 h-3.5" />
              {showHistory ? "Verlauf ausblenden" : "Verlauf anzeigen"}
              <ChevronDown className={`w-3 h-3 transition-transform ${showHistory ? "rotate-180" : ""}`} />
            </button>
            {showHistory && (
              <div className="mt-3 space-y-2 max-h-60 overflow-y-auto" data-testid="task-history">
                {loadingHistory ? (
                  <div className="text-center py-4"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mx-auto" /></div>
                ) : history.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-3">Keine Änderungen protokolliert</p>
                ) : (
                  history.map((h) => {
                    const isPhoto = h.action === "photo_added" || h.action === "photo_deleted";
                    return (
                      <div key={h.id} className="flex gap-2.5 text-xs">
                        <div className="flex flex-col items-center">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${isPhoto ? "bg-blue-400" : "bg-gray-600"}`} />
                          <div className="w-px flex-1 bg-white/10 mt-1" />
                        </div>
                        <div className="pb-3 min-w-0">
                          <p className="font-medium text-gray-300">
                            {isPhoto ? (h.action === "photo_added" ? "Foto hinzugefügt" : "Foto gelöscht")
                              : <>{h.field}: <span className="text-gray-500">{h.old_value || "–"}</span> → <span className="text-white">{h.new_value || "–"}</span></>}
                          </p>
                          <p className="text-gray-600 mt-0.5">
                            {h.changed_by_name || "System"} &middot; {h.timestamp ? format(new Date(h.timestamp), "dd.MM. HH:mm", { locale: de }) : ""}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* ── Kommentare / Rückmeldungen ── */}
          <TaskCommentsSection projectId={projectId} taskId={task.id} />
        </div>
      </DialogContent>
      <PhotoSourcePicker
        open={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onFilesSelected={(e) => { setShowPhotoPicker(false); handlePhotoUpload(e); }}
      />
    </Dialog>
  );
}

/* ═══════════════ MAIN EXPORT ═══════════════ */
export default function MobileTaskList({ projectId }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [mode, setMode] = useState("single");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", area_location: "", assigned_users: [], priority: "normal", task_location_marker: null });
  const [batchForm, setBatchForm] = useState({ base_title: "", count: 10 });
  const [users, setUsers] = useState([]);
  const cardCameraRef = useRef(null);
  const cardGalleryRef = useRef(null);
  const [photoTask, setPhotoTask] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCardPhotoPicker, setShowCardPhotoPicker] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [requirePhoto, setRequirePhoto] = useState(false);

  const loadTasks = useCallback(async () => {
    try {
      const res = await offlineAuthFetch(
        `${API}/projects/${projectId}/worker-tasks?filter_type=all`, {},
        { cacheKey: `tasks-${projectId}` }
      );
      if (res.ok) { const d = await res.json(); setTasks(d.tasks || []); }
    } catch {} finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => {
    offlineAuthFetch(`${API}/users/directory`, {}, { cacheKey: "users-directory" })
      .then(r => r.ok ? r.json() : []).then(setUsers).catch(() => {});
    // Load project settings for require_photo
    offlineAuthFetch(`${API}/projects/${projectId}`, {}, { cacheKey: `project-${projectId}` })
      .then(r => r.ok ? r.json() : null).then(p => {
        if (p) setRequirePhoto(!!p.require_photo_for_completion);
      }).catch(() => {});
  }, [projectId]);

  /* WhatsApp tap status */
  const tapStatus = async (task) => {
    const idx = STATUS_FLOW.indexOf(task.status);
    const next = STATUS_FLOW[(idx + 1) % STATUS_FLOW.length];
    // Foto-Pflicht check
    if (next === "erledigt" && requirePhoto && (!task.photos || task.photos.length === 0)) {
      toast.error("Foto-Pflicht: Mindestens ein Foto erforderlich");
      return;
    }
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: next } : t));
    const res = await offlineAuthFetch(
      `${API}/projects/${projectId}/tasks/${task.id}`,
      { method: "PATCH", body: JSON.stringify({ status: next }) },
      { type: "task-status", meta: { taskId: task.id, status: next }, optimisticResponse: { id: task.id, status: next } }
    );
    if (!res.ok && !res.headers?.get("X-Offline-Queued")) {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
      try {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      } catch { toast.error("Fehler"); }
    } else if (res.headers?.get("X-Offline-Queued")) {
      toast.success("Status lokal gespeichert");
    }
  };

  const handleTaskUpdated = useCallback((updatedTask) => {
    setTasks(prev => prev.map(t => t.id === updatedTask.id ? updatedTask : t));
    setSelectedTask(updatedTask);
  }, []);

  /* Photo from card – stays on task list */
  const handlePhotoClick = (task) => { setPhotoTask(task); setShowCardPhotoPicker(true); };
  const handlePhotoFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !photoTask) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error(`${file.name} zu gross (max ${MAX_FILE_SIZE_MB} MB)`);
          continue;
        }
        const compressed = await compressImage(file, 0.75, 1600);
        if (!navigator.onLine) {
          await storeOfflinePhoto({ blob: compressed, filename: file.name, projectId, taskId: photoTask.id });
          toast.success("Foto lokal gespeichert (Upload bei Verbindung)");
        } else {
          const fd = new FormData(); fd.append("file", compressed);
          const res = await authFetch(`${API}/projects/${projectId}/tasks/${photoTask.id}/photos`, { method: "POST", body: fd, headers: {} });
          if (res.ok) { toast.success("Foto gespeichert"); } else { toast.error("Upload fehlgeschlagen"); }
        }
      }
      loadTasks();
    } catch { toast.error("Fehler"); }
    finally {
      setUploading(false); setPhotoTask(null);
      if (cardCameraRef.current) cardCameraRef.current.value = "";
      if (cardGalleryRef.current) cardGalleryRef.current.value = "";
    }
  };

  /* Voice Recording */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { toast.error("Aufnahme zu kurz"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const res = await authFetch(`${API}/projects/${projectId}/voice-to-task`, { method: "POST", body: fd, headers: {} });
          if (res.ok) {
            const data = await res.json();
            if (data.parsed) {
              setForm(p => ({
                ...p,
                title: data.parsed.title || data.text,
                description: data.parsed.description || "",
              }));
              const prio = data.parsed.priority;
              if (prio === "hoch" || prio === "niedrig") {
                setForm(p => ({ ...p, priority: prio }));
              }
              toast.success("Sprache erkannt & analysiert");
            } else if (data.text) {
              setForm(p => ({ ...p, title: data.text }));
              toast.success("Sprache erkannt");
            } else {
              toast.error("Keine Sprache erkannt");
            }
          } else { toast.error("Transkription fehlgeschlagen"); }
        } catch { toast.error("Fehler bei Spracherkennung"); }
        finally { setTranscribing(false); }
      };
      mediaRecorder.start();
      setRecording(true);
    } catch {
      toast.error("Mikrofon nicht verfuegbar");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  /* Create single */
  const createSingle = async () => {
    if (!form.title.trim()) return toast.error("Titel erforderlich");
    setCreating(true);
    try {
      const body = { project_id: projectId, title: form.title, description: form.description, area_location: form.area_location || "", assigned_users: form.assigned_users || [], assigned_to: (form.assigned_users && form.assigned_users[0]) || null, status: "offen", priority: form.priority || "normal", task_location_marker: form.task_location_marker || null };
      const tempId = `offline_${Date.now()}`;
      const res = await offlineAuthFetch(`${API}/projects/${projectId}/tasks`, {
        method: "POST", body: JSON.stringify(body),
      }, {
        type: "task-create",
        meta: { projectId },
        optimisticResponse: { ...body, id: tempId, task_number: "...", created_at: new Date().toISOString(), photos: [], photo_count: 0 },
      });
      if (res.ok) {
        const t = await res.json();
        setTasks(prev => [t, ...prev]);
        toast.success(t.offline ? "Aufgabe lokal gespeichert" : "Aufgabe erstellt");
        setShowCreate(false);
        setForm({ title: "", description: "", area_location: "", assigned_users: [], task_location_marker: null });
      } else { const err = await res.json(); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Fehler"); } finally { setCreating(false); }
  };

  /* Create batch */
  const createBatch = async () => {
    if (!batchForm.base_title.trim()) return toast.error("Name erforderlich");
    setCreating(true);
    try {
      const res = await offlineAuthFetch(`${API}/projects/${projectId}/tasks/batch`, {
        method: "POST", body: JSON.stringify({ base_title: batchForm.base_title, count: parseInt(batchForm.count), priority: "normal" }),
      }, { type: "task-batch", meta: { projectId } });
      if (res.ok) {
        const d = await res.json();
        if (d.tasks) setTasks(prev => [...d.tasks, ...prev]);
        toast.success(d.offline ? "Batch lokal gespeichert" : `${d.created || batchForm.count} Aufgaben erstellt`);
        setShowCreate(false);
        setBatchForm({ base_title: "", count: 10 });
      } else { const err = await res.json(); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Fehler"); } finally { setCreating(false); }
  };

  const filtered = tasks.filter(t => {
    if (filter === "all") return true;
    if (filter === "mine") return t.assigned_to === user?.id || t.created_by === user?.id || (Array.isArray(t.assigned_users) && t.assigned_users.includes(user?.id));
    return t.status === filter;
  });

  const filters = [
    { v: "all", l: t("mt.filter_all", "Alle") }, { v: "mine", l: t("mt.filter_mine", "Meine") },
    { v: "offen", l: t("mt.filter_open", "Offen") }, { v: "in_arbeit", l: t("mt.filter_in_progress", "In Arbeit") }, { v: "erledigt", l: t("mt.filter_done", "Erledigt") },
  ];

  return (
    <div data-testid="m-task-list">
      <input ref={cardCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoFile} />
      <input ref={cardGalleryRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoFile} />
      <PhotoSourcePicker
        open={showCardPhotoPicker}
        onClose={() => { setShowCardPhotoPicker(false); setPhotoTask(null); }}
        onFilesSelected={(e) => { setShowCardPhotoPicker(false); handlePhotoFile(e); }}
      />

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[15px] font-bold text-white flex items-center gap-2">
          <ListTodo className="w-4 h-4 text-blue-400" />
          {t("mp.tab_tasks", "Aufgaben")}
          <span className="text-xs font-normal text-gray-500">({tasks.length})</span>
        </h2>
        <button onClick={() => { setShowCreate(true); setMode("single"); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold active:bg-blue-700"
          data-testid="m-add-task-btn">
          <Plus className="w-3.5 h-3.5" />{t("mt.add_btn", "Aufgabe")}
        </button>
      </div>

      <DarkProgressBar tasks={tasks} />

      {tasks.length > 0 && (
        <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
          {filters.map(f => (
            <button key={f.v} onClick={() => setFilter(f.v)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f.v ? "bg-white text-gray-900" : "bg-white/8 text-gray-400 border border-white/10"
              }`} data-testid={`m-filter-${f.v}`}>{f.l}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8">
          <ListTodo className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{tasks.length === 0 ? t("mt.no_tasks", "Noch keine Aufgaben") : t("mt.no_match_filter", "Keine mit diesem Filter")}</p>
        </div>
      ) : (
        <div className="space-y-2">{filtered.map(t => (
          <DarkTaskCard key={t.id} task={t} onTapStatus={tapStatus} onSelect={setSelectedTask} onPhotoClick={handlePhotoClick} />
        ))}</div>
      )}

      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900" />
            <span className="text-sm font-medium">{t("mt.photo_upload", "Foto wird hochgeladen...")}</span>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#1a1b2e] border-white/10 text-white" data-testid="m-task-create-modal">
          <DialogHeader><DialogTitle className="text-white">{mode === "single" ? t("mt.new_task", "Neue Aufgabe") : t("mt.quick_mode", "Schnellmodus")}</DialogTitle></DialogHeader>
          <div className="flex rounded-xl bg-white/10 p-1 gap-1">
            <button onClick={() => setMode("single")} className={`flex-1 py-2 text-sm font-medium rounded-lg ${mode === "single" ? "bg-white/20 shadow-sm text-white" : "text-gray-400"}`} data-testid="m-mode-single">{t("mt.single_mode", "Einzeln")}</button>
            <button onClick={() => setMode("batch")} className={`flex-1 py-2 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5 ${mode === "batch" ? "bg-white/20 shadow-sm text-white" : "text-gray-400"}`} data-testid="m-mode-batch">
              <Layers className="w-3.5 h-3.5" />{t("mt.quick_mode", "Schnellmodus")}
            </button>
          </div>
          {mode === "single" ? (
            <div className="space-y-3">
              <div>
                <Label className="text-gray-400">Titel *</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="z.B. Aussenleuchte montieren" autoFocus className="flex-1 bg-white/10 border-white/15 text-white placeholder:text-gray-500" data-testid="m-task-title" />
                  <button
                    type="button"
                    onClick={recording ? stopRecording : startRecording}
                    disabled={transcribing}
                    className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      recording ? "bg-red-500 animate-pulse text-white" : transcribing ? "bg-gray-600 text-gray-400" : "bg-white/10 text-gray-400 hover:bg-white/15 hover:text-white"
                    }`}
                    data-testid="m-voice-btn"
                  >
                    {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                </div>
                {recording && <p className="text-xs text-red-400 mt-1 animate-pulse">Aufnahme laeuft... Tippe zum Stoppen</p>}
                {transcribing && <p className="text-xs text-blue-400 mt-1">Wird transkribiert...</p>}
              </div>
              <div><Label className="text-gray-400">Beschreibung</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional" rows={2} className="bg-white/10 border-white/15 text-white placeholder:text-gray-500" /></div>
              <div>
                <Label className="text-gray-400">Bereich / Ort im Projekt (optional)</Label>
                <Input
                  value={form.area_location}
                  onChange={e => setForm(p => ({ ...p, area_location: e.target.value }))}
                  placeholder="z. B. Hinterer Technikbereich, Halle 2"
                  className="mt-1 bg-white/10 border-white/15 text-white placeholder:text-gray-500"
                  data-testid="m-task-area-location"
                />
              </div>
              <div>
                <Label className="text-gray-400">Standort auf Plan (optional)</Label>
                <div className="mt-1">
                  <TaskMarkerPicker
                    projectId={projectId}
                    marker={form.task_location_marker}
                    onChange={(m) => setForm(p => ({ ...p, task_location_marker: m }))}
                    theme="dark"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-400">Zuweisen an (Mehrfach möglich)</Label>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="m-new-assign-chips">
                  {users.map(u => {
                    const selected = (form.assigned_users || []).includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setForm(p => {
                          const cur = p.assigned_users || [];
                          const next = cur.includes(u.id) ? cur.filter(x => x !== u.id) : [...cur, u.id];
                          return { ...p, assigned_users: next };
                        })}
                        className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          selected
                            ? "bg-blue-500/30 border-blue-400/60 text-blue-100"
                            : "bg-white/5 border-white/10 text-gray-300 active:bg-white/10"
                        }`}
                        data-testid={`m-new-assign-chip-${u.id}`}
                      >
                        {selected && "✓ "}{u.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button onClick={createSingle} disabled={creating} className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-50" data-testid="m-create-task-btn">
                {creating ? "Erstelle..." : "Aufgabe erstellen"}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-blue-500/15 border border-blue-500/30 rounded-xl p-3">
                <p className="text-sm text-blue-300 font-medium">Schnellmodus</p>
                <p className="text-xs text-blue-400 mt-0.5">Name + Anzahl → automatisch nummerierte Aufgaben.</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label className="text-gray-400">Name *</Label><Input value={batchForm.base_title} onChange={e => setBatchForm(p => ({ ...p, base_title: e.target.value }))} placeholder="z.B. Außenleuchte" className="bg-white/10 border-white/15 text-white placeholder:text-gray-500" data-testid="m-batch-title" /></div>
                <div><Label className="text-gray-400">Anzahl</Label><Input type="number" min={1} max={100} value={batchForm.count} onChange={e => setBatchForm(p => ({ ...p, count: e.target.value }))} className="bg-white/10 border-white/15 text-white placeholder:text-gray-500" data-testid="m-batch-count" /></div>
              </div>
              {batchForm.base_title && (
                <div className="bg-white/5 rounded-lg p-3 text-xs text-gray-400">
                  {Array.from({ length: Math.min(3, parseInt(batchForm.count) || 0) }, (_, i) => `"${batchForm.base_title} ${i + 1}"`).join(", ")}
                  {parseInt(batchForm.count) > 3 && ` ... "${batchForm.base_title} ${batchForm.count}"`}
                </div>
              )}
              <button onClick={createBatch} disabled={creating} className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="m-create-batch-btn">
                <Layers className="w-4 h-4" />{creating ? "Erstelle..." : `${batchForm.count} Aufgaben erstellen`}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask} open={!!selectedTask} onClose={() => setSelectedTask(null)}
        projectId={projectId} onTaskUpdated={handleTaskUpdated} users={users}
      />
    </div>
  );
}
